import { useEffect, useRef, useState } from 'react'
import { Panel, useNodesInitialized, useReactFlow, useStore, useViewport } from 'reactflow'
import { ZoomControls } from '@/components/intueri/ZoomControls'
import { useZoom } from '@/hooks/useZoom'
import { ZOOM_MAX_PERCENT_VECTOR } from '@/constants/zoom'

// Wider native range than the display range so the 20%–200% display window
// stays reachable even for tiny/huge fit baselines.
export const CANVAS_MIN_ZOOM = 0.05
export const CANVAS_MAX_ZOOM = 3
/** Mirrors fitViewOptions: fit uses 0.2 padding and never zooms past 1:1. */
const FIT_PADDING = 0.2
const FIT_MAX_ZOOM = 1

/**
 * Bottom-left canvas zoom control (Figma "3. Create Workflow"). This is the
 * SAME pill the board uses — the shared <ZoomControls> primitive + `useZoom`
 * (FS-562) — so the chrome, readout type-in, 10% steps and 20%–200% clamps are
 * byte-identical across every zoomable surface (Pegah, workflow feedback
 * 2026-07-14 — "zoom not same as board").
 *
 * Uses the LINEAR `percent` model like the board: the fit reads 100%, 2× the
 * fit 200%, half the fit 50% — the natural model for an infinite pan/zoom
 * canvas. (The compressed `fit-baseline` model is for raster/P&ID, where 200%
 * means 2× the document's native pixels; on an infinite canvas it clamped
 * zoom-out to odd values — the actual "not same as board" behavior.)
 *
 * Undo/redo render disabled: the canvas is read-only (generation authors it).
 * The zoom that reads as 100% is captured AFTER ReactFlow measures + fits the
 * nodes (useNodesInitialized) and re-anchors when the layout changes.
 */
export default function CanvasControls() {
  const { zoomTo, fitView, getZoom } = useReactFlow()
  const { zoom } = useViewport()
  const nodesInitialized = useNodesInitialized()
  const nodeCount = useStore((s) => s.nodeInternals.size)
  // Layout identity = count + bounding extents. Keying the refit on COUNT
  // alone missed re-layouts that move the same nodes (the snake-density
  // setting turning two short columns into one tall one) — the taller column
  // then overflowed the viewport at the stale transform until the user
  // zoomed out by hand.
  const layoutKey = useStore((s) => {
    let maxX = 0
    let maxY = 0
    s.nodeInternals.forEach((node) => {
      maxX = Math.max(maxX, node.position.x)
      maxY = Math.max(maxY, node.position.y)
    })
    return `${s.nodeInternals.size}:${Math.round(maxX)}x${Math.round(maxY)}`
  })

  // The viewport zoom accepted as 100%. Captured AFTER ReactFlow has measured
  // the nodes and applied its fit (useNodesInitialized) — reading it earlier
  // is what produced the "opens at a weird percentage" bug.
  const [baseline, setBaseline] = useState(1)
  const baselineKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!nodesInitialized || nodeCount === 0) return
    if (baselineKeyRef.current === layoutKey) return
    // Nodes that arrive after mount (generated draft / opened workflow) aren't
    // covered by the initial fitView prop — fit explicitly, then accept the
    // result as 100%. TWO hard-won rules here:
    //  1. DEFER the fit one frame: ReactFlow v11's fitView silently no-ops
    //     when called in the same tick useNodesInitialized flips (the zoom
    //     pane isn't ready). The failure was invisible — getZoom() then reads
    //     1, the baseline becomes 1, and the readout says "100%" as if the
    //     fit worked. Short 4-row layouts roughly fit at 1:1 which masked
    //     this; the 8-row single column overflowed the viewport and exposed it.
    //  2. Mark the layout as fitted ONLY when the fit actually APPLIES
    //     (inside the frame callback) — marking up-front + the cleanup
    //     cancel is the exact StrictMode double-mount trap that broke
    //     template hydration: run #1 marks and is cancelled, run #2 bails.
    // No `duration` on the fit: an animated fitView applies the transform
    // asynchronously, and getZoom() must read the FITTED zoom next line.
    // RETRY until ReactFlow accepts the fit: v11's fitView returns FALSE
    // (probe-verified) while the just-replaced nodes re-measure — even a
    // frame after useNodesInitialized flips. Marking the layout as fitted
    // only on SUCCESS keeps a rejected attempt retryable; the bounded budget
    // (~1s) prevents a pathological loop if the pane never becomes fittable.
    let cancelled = false
    let attempts = 0
    const tryFit = () => {
      if (cancelled) return
      const applied = fitView({ padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM })
      if (applied) {
        baselineKeyRef.current = layoutKey
        setBaseline(getZoom() || 1)
        return
      }
      attempts += 1
      if (attempts < 60) requestAnimationFrame(tryFit)
    }
    const frame = requestAnimationFrame(tryFit)
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [nodesInitialized, nodeCount, layoutKey, fitView, getZoom])

  const zoomApi = useZoom({
    model: 'percent',
    scale: zoom,
    baseline,
    maxPercent: ZOOM_MAX_PERCENT_VECTOR,
    onApply: (nextScale) => zoomTo(nextScale, { duration: 120 }),
  })

  return (
    <Panel position="bottom-left">
      <ZoomControls
        percent={zoomApi.percent}
        onZoomIn={zoomApi.zoomIn}
        onZoomOut={zoomApi.zoomOut}
        onSetPercent={zoomApi.setPercent}
        canZoomIn={zoomApi.canZoomIn}
        canZoomOut={zoomApi.canZoomOut}
        onFit={() => fitView({ padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: 120 })}
        // Read-only canvas: undo/redo are shown (board parity) but disabled.
        undoRedo={{ onUndo: () => {}, onRedo: () => {}, canUndo: false, canRedo: false }}
      />
    </Panel>
  )
}
